import { all, call, put, takeLatest } from 'redux-saga/effects';
import { AxiosCall, AxiosPost, AxiosPatch, AxiosDelete, AxiosFile, AxiosFilePatch } from '../../../utils/axios';
import * as actions from './organizationActions';

// Get My Organization
function* getMyOrganization() {
  try {
    const result = yield call(AxiosCall, '/mycore/org/me/');
    yield put(actions.getMyOrganizationSuccess(result.data));
  } catch (e) {
    yield put(actions.getMyOrganizationFailure(e.response?.data?.error || e.message));
  }
}

// Create Organization
function* createOrganization(action) {
  try {
    const formData = new FormData();

    // Handle image upload
    if (action.payload.image && action.payload.image instanceof File) {
      formData.append('image', action.payload.image);
      formData.append('name', action.payload.name);
      if (action.payload.active !== undefined) {
        formData.append('active', action.payload.active);
      }
      // Batch code configuration
      if (action.payload.batch_code_prefix) {
        formData.append('batch_code_prefix', action.payload.batch_code_prefix);
      }
      if (action.payload.batch_code_next_number) {
        formData.append('batch_code_next_number', action.payload.batch_code_next_number);
      }
      if (action.payload.batch_code_padding) {
        formData.append('batch_code_padding', action.payload.batch_code_padding);
      }
      const result = yield call(AxiosFile, '/mycore/org/add/', formData);
      yield put(actions.createOrganizationSuccess(result.data));
    } else {
      // No image, send as JSON
      const result = yield call(AxiosPost, '/mycore/org/add/', action.payload);
      yield put(actions.createOrganizationSuccess(result.data));
    }
  } catch (e) {
    yield put(actions.createOrganizationFailure(e.response?.data?.error || e.message));
  }
}

// Update Organization
function* updateOrganization(action) {
  try {
    const { id, data } = action.payload;

    // Handle image upload
    if (data.image && data.image instanceof File) {
      const formData = new FormData();
      formData.append('image', data.image);
      formData.append('name', data.name);
      if (data.active !== undefined) {
        formData.append('active', data.active);
      }
      // Batch code configuration
      if (data.batch_code_prefix !== undefined) {
        formData.append('batch_code_prefix', data.batch_code_prefix);
      }
      if (data.batch_code_next_number !== undefined) {
        formData.append('batch_code_next_number', data.batch_code_next_number);
      }
      if (data.batch_code_padding !== undefined) {
        formData.append('batch_code_padding', data.batch_code_padding);
      }
      const result = yield call(AxiosFilePatch, `/mycore/${id}/org/update/`, formData);
      yield put(actions.updateOrganizationSuccess(result.data));
    } else {
      // No new image - remove image field if it's a URL string (existing image)
      const updateData = { ...data };
      if (typeof updateData.image === 'string') {
        delete updateData.image;
      }
      const result = yield call(AxiosPatch, `/mycore/${id}/org/update/`, updateData);
      yield put(actions.updateOrganizationSuccess(result.data));
    }
  } catch (e) {
    yield put(actions.updateOrganizationFailure(e.response?.data?.error || e.message));
  }
}

// Delete Organization
function* deleteOrganization(action) {
  try {
    const id = action.payload;
    yield call(AxiosDelete, `/mycore/${id}/org/delete/`);
    yield put(actions.deleteOrganizationSuccess(id));
  } catch (e) {
    yield put(actions.deleteOrganizationFailure(e.response?.data?.error || e.message));
  }
}

// Invite Member
function* inviteMember(action) {
  try {
    const { organizationId, email, roleId } = action.payload;
    const result = yield call(AxiosPost, `/mycore/${organizationId}/org/invite/`, {
      email,
      role_id: roleId,
    });
    yield put(actions.inviteMemberSuccess(result.data));
  } catch (e) {
    yield put(actions.inviteMemberFailure(e.response?.data?.error || e.message));
  }
}

// Accept Invitation
function* acceptInvitation(action) {
  try {
    const token = action.payload;
    const result = yield call(AxiosPost, '/mycore/org/accept-invite/', { token });
    yield put(actions.acceptInvitationSuccess(result.data));
  } catch (e) {
    yield put(actions.acceptInvitationFailure(e.response?.data?.error || e.message));
  }
}

// Reject Invitation
function* rejectInvitation(action) {
  try {
    const token = action.payload;
    yield call(AxiosPost, '/mycore/org/reject-invite/', { token });
    yield put(actions.rejectInvitationSuccess());
  } catch (e) {
    yield put(actions.rejectInvitationFailure(e.response?.data?.error || e.message));
  }
}

// Get Members
function* getMembers(action) {
  try {
    const organizationId = action.payload;
    const result = yield call(AxiosCall, `/mycore/${organizationId}/org/members/`);
    yield put(actions.getMembersSuccess(result.data));
  } catch (e) {
    yield put(actions.getMembersFailure(e.response?.data?.error || e.message));
  }
}

// Change Member Role
function* changeMemberRole(action) {
  try {
    const { organizationId, memberId, roleId } = action.payload;
    const result = yield call(AxiosPost, `/mycore/${organizationId}/org/change-role/`, {
      member_id: memberId,
      role_id: roleId,
    });
    yield put(actions.changeMemberRoleSuccess(result.data));
  } catch (e) {
    yield put(actions.changeMemberRoleFailure(e.response?.data?.error || e.message));
  }
}

// Remove Member
function* removeMember(action) {
  try {
    const { organizationId, memberId } = action.payload;
    yield call(AxiosPost, `/mycore/${organizationId}/org/remove-member/`, {
      member_id: memberId,
    });
    yield put(actions.removeMemberSuccess(memberId));
  } catch (e) {
    yield put(actions.removeMemberFailure(e.response?.data?.error || e.message));
  }
}

// Get Available Roles
function* getAvailableRoles() {
  try {
    const result = yield call(AxiosCall, '/mycore/org/available-roles/');
    yield put(actions.getAvailableRolesSuccess(result.data));
  } catch (e) {
    yield put(actions.getAvailableRolesFailure(e.response?.data?.error || e.message));
  }
}

// Root Saga
export default function* organizationSaga() {
  yield all([
    takeLatest(actions.GET_MY_ORGANIZATION.REQUEST, getMyOrganization),
    takeLatest(actions.CREATE_ORGANIZATION.REQUEST, createOrganization),
    takeLatest(actions.UPDATE_ORGANIZATION.REQUEST, updateOrganization),
    takeLatest(actions.DELETE_ORGANIZATION.REQUEST, deleteOrganization),
    takeLatest(actions.INVITE_MEMBER.REQUEST, inviteMember),
    takeLatest(actions.ACCEPT_INVITATION.REQUEST, acceptInvitation),
    takeLatest(actions.REJECT_INVITATION.REQUEST, rejectInvitation),
    takeLatest(actions.GET_MEMBERS.REQUEST, getMembers),
    takeLatest(actions.CHANGE_MEMBER_ROLE.REQUEST, changeMemberRole),
    takeLatest(actions.REMOVE_MEMBER.REQUEST, removeMember),
    takeLatest(actions.GET_AVAILABLE_ROLES.REQUEST, getAvailableRoles),
  ]);
}
